/**
 * Bridge between the axios interceptor (plain module, no React context) and the
 * session-expired modal mounted at the app root.
 *
 * The backend answers with `code: "SESSION_REVOKED"` once an account signs in
 * somewhere else. Every in-flight request on the old device fails at once, so
 * the broker latches: only the first one raises the event, and it stays latched
 * until the page navigates away.
 */

import { isPaired, clearDriverSession, DEVICE_ROUTE } from "./deviceSession";

export const SESSION_REVOKED_EVENT = "livecargo:session-revoked";

export interface SessionRevokedDetail {
  message: string;
}

const DEFAULT_MESSAGE =
  "Your session ended because this account was signed in on another device.";

let alreadyRaised = false;
let alreadySentToKeypad = false;
let signingOut = false;

/**
 * How long a deliberate sign-out stays quiet.
 *
 * It only has to outlast the requests that were already in the air when Logout
 * was clicked, plus the socket frame — hundreds of milliseconds. Ten seconds is
 * generous for that and still far too short to reach into whatever the user
 * does next.
 */
const LOGOUT_QUIET_MS = 10_000;

let quietTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Mark a sign-out the user asked for, so nothing explains it to them.
 *
 * The socket frame is not the only way the modal gets raised. Clearing the
 * session also kills every request already in flight when Logout was clicked,
 * and `authenticate` answers those with the same `SESSION_REVOKED` the
 * interceptor turns into the modal — so suppressing the socket alone would
 * leave the same wrong message arriving a few milliseconds later, by a
 * different road and only when the timing was unlucky.
 *
 * ── Why this expires rather than latching ───────────────────────────────────
 *
 * It latched once, on the reasoning that the window ends when the page
 * navigates away. It does not: `handleLogout` in Navbar.tsx finishes with
 * `navigate("/login")`, which is a router transition, so no module state is
 * discarded. The flag survived into the next session and suppressed a genuine
 * displacement — the user stayed on the dashboard holding a dead session while
 * the raw 401 leaked out as a toast, because the modal that was supposed to
 * explain it had been silenced hours earlier by their own previous logout.
 *
 * `SessionExpiredModal` hides the same flaw in `alreadyRaised`: its button
 * assigns `window.location.href`, a real page load, which clears everything.
 * Nothing on the logout path does that, so this cannot borrow the same luck.
 *
 * A timer is used rather than only clearing on login because there are three
 * ways back in — `login`, `pinLogin`, `loginSuperAdmin` — and a correctness
 * guarantee that depends on every future entry point remembering to call a
 * reset is not a guarantee. `resetSessionNotices` still runs on each of them
 * so the common path is exact; this is what makes the uncommon path safe.
 */
export const beginDeliberateLogout = () => {
  signingOut = true;

  if (quietTimer) clearTimeout(quietTimer);
  quietTimer = setTimeout(() => {
    signingOut = false;
    quietTimer = null;
  }, LOGOUT_QUIET_MS);
};

/**
 * A new session is starting — drop everything the previous one latched.
 *
 * Called at the top of every login thunk, before credentials are exchanged, so
 * it runs whether or not the attempt succeeds.
 */
export const resetSessionNotices = () => {
  signingOut = false;
  alreadyRaised = false;
  alreadySentToKeypad = false;

  if (quietTimer) {
    clearTimeout(quietTimer);
    quietTimer = null;
  }
};

/** Clear credentials and raise the event exactly once. */
export const notifySessionRevoked = (message?: string) => {
  // The user is leaving on purpose. Credentials are the logout thunk's job
  // here, and there is nothing to explain.
  if (signingOut) return;

  const subdomain = window.location.hostname.split(".")[0];
  if (subdomain === "admin") {
    localStorage.removeItem("superAdminToken");
    localStorage.removeItem("superAdmin");
  } else {
    /**
     * A truck's screen is never shown the password modal.
     *
     * `SessionExpiredModal` is blocking and its only action is a link to
     * `/login` — a form with an email field, on a tablet bolted into a cab,
     * with no keyboard in reach and no password anybody in the yard knows. It
     * is the exact dead end this codebase goes to some lengths to avoid
     * elsewhere (see DEVICE_SESSION_ENDPOINTS in services/api.ts), and the
     * device paths were careful never to produce it.
     *
     * But `SESSION_REVOKED` does not come from the device paths. It comes from
     * `authenticate`, which rejects any token whose session has been ended —
     * and ending a driver's session is exactly what the office does when it
     * revokes a device, signs a driver out, or reissues a pairing code. So all
     * three landed a cab screen in the modal, and the interceptor checks
     * `isSessionRevoked` *before* the device codes, so the device handling
     * never got a look in.
     *
     * It became immediate rather than merely likely when `endSession` started
     * severing sockets: the tablet no longer waits for its next request to find
     * out, it is told, and the modal appeared under the driver's hand.
     *
     * The recovery for a cab screen is the keypad, which is also where the
     * device's own state gets re-checked on arrival: `/truck` mounts,
     * `refreshContext` runs, and a pairing that died with the session turns
     * into the pairing form from there. So this hands over rather than
     * explaining anything — the next screen is the explanation.
     *
     * ── Why the test is the pairing and not the session ─────────────────────
     *
     * `hasDeviceSession()` is the precise question ("was this a PIN session?")
     * and it was the first thing tried here. It cannot be used: the marker is
     * dropped by `clearDriverSession`, and an ordinary End-shift tap races
     * against it — the server ends the session, the socket says so immediately,
     * and whether the marker is still there depends on which of a socket frame
     * and an HTTP response lands first. Lose that race and a driver tapping
     * sign out gets the password modal, which is a worse bug than the one being
     * fixed and far harder to reproduce.
     *
     * So the test is a fact that does not move: this browser holds a device
     * pairing, therefore it is a truck's screen, therefore its home is the
     * keypad. The cost is a dispatcher who signs in with a password on a cab
     * tablet and is displaced: they are sent to the keypad instead of the
     * modal, and have to type `/login` to get back. Rare, recoverable, and the
     * right way round — the daily case is a driver in a cab, and for them the
     * modal is not recoverable at all.
     */
    const cabScreen = isPaired();

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    if (cabScreen) {
      // The driver's credentials go; the pairing is left alone. Only the server
      // saying DEVICE_UNPAIRED may clear that, and if it is about to, it will.
      clearDriverSession();

      if (
        !alreadySentToKeypad &&
        !window.location.pathname.startsWith(DEVICE_ROUTE)
      ) {
        alreadySentToKeypad = true;
        window.location.href = DEVICE_ROUTE;
      }

      // Deliberately not latched as "raised": nothing was raised. Every other
      // in-flight 401 on this tablet takes this same branch and is a no-op.
      return;
    }
  }

  if (alreadyRaised) return;
  alreadyRaised = true;

  window.dispatchEvent(
    new CustomEvent<SessionRevokedDetail>(SESSION_REVOKED_EVENT, {
      detail: { message: message || DEFAULT_MESSAGE },
    }),
  );
};

/** True when the response is a revoked session rather than an expired token. */
export const isSessionRevoked = (error: any): boolean =>
  error?.response?.status === 401 &&
  error?.response?.data?.code === "SESSION_REVOKED";
